import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, Search, ChevronLeft, ChevronRight, Table2, X } from 'lucide-react';

const CurrentStockPosition = ({ data = [] }) => {
  // State management for search, sorting, and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'StockValue', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showTable, setShowTable] = useState(false);
  const itemsPerPage = 10;

  // 1. Calculate Top-Level Executive KPI Metrics
  const metrics = useMemo(() => {
    return data.reduce(
      (acc, item) => {
        acc.totalQty += item.CurrentQty || 0;
        acc.totalValue += item.StockValue || 0;
        return acc;
      },
      { totalQty: 0, totalValue: 0 }
    );
  }, [data]);

  // 2. Handle Sorting Logic
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 3. Filter and Sort the Data Array
  const processedData = useMemo(() => {
    // Filter by search term
    let filtered = data.filter((item) =>
      item.GROUP?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort items
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sortConfig.direction === 'asc' 
            ? aValue - bValue 
            : bValue - aValue;
        }
      });
    }
    return filtered;
  }, [data, searchTerm, sortConfig]);

  // 4. Pagination Calculations
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return processedData.slice(start, start + itemsPerPage);
  }, [processedData, currentPage]);

  // Reset page when search changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Helper component for sort indicators
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <div className="w-4 h-4 opacity-30"><ChevronUp className="w-4 h-4" /></div>;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-indigo-600" />
      : <ChevronDown className="w-4 h-4 text-indigo-600" />;
  };

  return (
    <div className="w-full space-y-2 p-1 bg-gray-50/50 rounded-xl">
      
        {/* --- TABLE TOGGLE BUTTON --- */}
        <div className="">
        <button
          onClick={() => setShowTable((prev) => !prev)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition-colors"
        >
          {showTable ? <X className="w-4 h-4" /> : <Table2 className="w-4 h-4" />}
          {showTable ? 'Hide Stock Table' : 'Show Stock Table'}
        </button>
      </div>

 {/* --- TABLE CONTROLS & SEARCH --- */}
 {showTable && (
      <div className="w-full max-w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" >
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
         
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by group/brand..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
            />
          </div>
        </div>

        {/* --- DATA TABLE --- */}
        <div className="overflow-x-auto " style={{ height:"200px"}}>
          <table className=" text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-600 tracking-wider">
                <th onClick={() => handleSort('GROUP')} className="p-4 cursor-pointer hover:bg-gray-100 select-none transition-colors">
                  <div className="flex items-center gap-1">Group / Brand <SortIcon columnKey="GROUP" /></div>
                </th>
                <th onClick={() => handleSort('CurrentQty')} className="p-4 text-right cursor-pointer hover:bg-gray-100 select-none transition-colors">
                  <div className="flex items-center justify-end gap-1">Current Qxl <SortIcon columnKey="CurrentQty" /></div>
                </th>
                <th onClick={() => handleSort('StockValue')} className="p-4 text-right cursor-pointer hover:bg-gray-100 select-none transition-colors">
                  <div className="flex items-center justify-end gap-1">Stock Value <SortIcon columnKey="StockValue" /></div>
                </th>
                <th className="p-4 text-right font-medium text-gray-500">
                  Avg Unit Value
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const avgUnitValue = item.CurrentQty > 0 ? item.StockValue / item.CurrentQty : 0;
                  return (
                    <tr key={item.GROUP || index} className="hover:bg-gray-50/70 transition-colors">
                      <td className="p-4 font-semibold text-gray-900">{item.GROUP}</td>
                      <td className="p-4 text-right font-mono">
                        {item.CurrentQty.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-4 text-right font-mono font-medium text-emerald-700">
                        {item.StockValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-4 text-right font-mono text-gray-500">
                        {avgUnitValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-gray-400">
                    No matching inventory records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* --- PAGINATION CONTROL FOOTER --- */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50/50">
            <span className="text-xs text-gray-500">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, processedData.length)} to{' '}
              {Math.min(currentPage * itemsPerPage, processedData.length)} of {processedData.length} records
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:hover:bg-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      )}
      
      {/* --- EXECUTIVE KPI CARDS --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
        <div className="bg-white px-4 py-2 rounded-xl border border-gray-200/80 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Stock Value</p>
          <p className="text-xl font-bold text-gray-900 mt-1">
            {metrics.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        
        <div className="bg-white px-4 py-2  rounded-xl border border-gray-200/80 shadow-sm">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Current Qty</p>
          <p className="text-xl font-bold text-gray-900 mt-1">
            {metrics.totalQty.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-white px-4 py-2  rounded-xl border border-gray-200/80 shadow-sm">
          <p className="text-xs  text-gray-500 uppercase tracking-wider">Active Inventory Groups</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{data.length}</p>
        </div>
      </div>

    

     
    </div>
  );
};

export default CurrentStockPosition;